import type { JSX } from 'react'
import { useEffect, useRef } from 'react'
import { Eye, Pencil, Play, RotateCw, Square, Trash2 } from 'lucide-react'
import { MINIMAP_SIZE, type MinimapFrame } from '@apiary/shared'
import { minimapImageData } from '@apiary/viewer-core'
import type { BotDto } from '@/lib/dto'
import { cn } from '@/lib/cn'
import { botStateTone } from '@/lib/format'
import { coordsOf, uptimeOf } from '@/ui/format'
import { Surface, OnTop } from '@/ui/primitives/Surface'
import { IconButton } from '@/ui/primitives/Button'
import { Menu } from '@/ui/primitives/Menu'
import { Chip } from '@/ui/primitives/Chip'
import type { StatusTone } from '@/ui/primitives/StatusDot'
import { SkinHead } from '@/ui/primitives/SkinHead'

/**
 * Плитка бота — главный объект интерфейса.
 *
 * Что здесь исправлено по сравнению с прошлой версией:
 *
 *  1. КЛИКАБЕЛЬНА ЦЕЛИКОМ. Раньше ссылкой было только название, и чтобы открыть
 *     бота, приходилось прицеливаться в текст. Теперь ссылка растянута на всю
 *     площадь (`Surface to=…`), а кнопки внутри поднятын над ней (`OnTop`).
 *  2. Меню действий уезжает в портал и не вылезает за границы карточки.
 *  3. Курсор клавиатурной навигации — тонкая рамка ВНУТРИ карточки и только
 *     после первого нажатия клавиши (см. `useKeyboardMode`), а не постоянная
 *     жёлтая обводка на первой плитке.
 *  4. Мини-карта занимает всю ширину и служит фоном для координат: она главный
 *     носитель информации «где бот», а не декоративный квадратик.
 */

export interface BotTileProps {
  bot: BotDto
  canOperate: boolean
  minimap?: MinimapFrame | null
  busy?: boolean
  /** Позиция курсора `j/k`. Видна только в клавиатурном режиме. */
  cursor?: boolean
  onStart: () => void
  onStop: () => void
  onRestart: () => void
  onEdit: () => void
  onDelete: () => void
  /** Открыть камеру бота. Без обработчика кнопка выключена. */
  onCamera?: () => void
}

const STATE_LABEL: Record<BotDto['state'], string> = {
  STOPPED: 'Остановлен',
  QUEUED: 'В очереди',
  CONNECTING: 'Подключение',
  LOGIN: 'Вход',
  CONFIG: 'Настройка',
  SPAWNING: 'Появление',
  PLAYING: 'В игре',
  DEGRADED: 'С оговорками',
  RECONNECT_WAIT: 'Ждёт связи',
  AUTH_FAILED: 'Ошибка входа',
  UNKNOWN: 'Неизвестно'
}

const TONE_MAP: Record<string, StatusTone> = {
  online: 'live',
  degraded: 'warn',
  connecting: 'busy',
  waiting: 'hold',
  offline: 'idle',
  danger: 'fail'
}

export function BotTile({
  bot,
  canOperate,
  minimap = null,
  busy = false,
  cursor = false,
  onStart,
  onStop,
  onRestart,
  onEdit,
  onDelete,
  onCamera
}: BotTileProps): JSX.Element {
  const tone = TONE_MAP[botStateTone(bot.state)] ?? 'idle'
  const detail = bot.lastDetail
  const coords = coordsOf(detail?.pos)
  const running = bot.state !== 'STOPPED' && bot.state !== 'AUTH_FAILED'
  const inFlight = tone === 'busy' || tone === 'hold'
  const noRights = canOperate ? undefined : 'Недостаточно прав'

  return (
    <Surface
      variant="raised"
      to="/bots/$botId"
      params={{ botId: bot.id }}
      activateLabel={`Открыть бота ${bot.name}`}
      className="group flex flex-col"
      data-bot-id={bot.id}
    >
      {/* Курсор j/k: рамка внутри карточки, не сдвигает раскладку. */}
      {cursor ? (
        <span
          data-cursor="on"
          aria-hidden
          className="pointer-events-none absolute inset-0 z-sticky rounded-md ring-2 ring-honey/70 ring-inset"
        />
      ) : null}

      <div className="flex min-w-0 items-start gap-3 px-3.5 pt-3">
        {/* Аватарка — настоящий скин аккаунта, объёмная. Ставится первой:
            в списке из двадцати ботов по лицу узнаёшь быстрее, чем по имени. */}
        <SkinHead
          uuid={bot.mcAccountUuid}
          name={detail?.username ?? bot.mcAccountLabel}
          size={38}
          spin={bot.state === 'PLAYING'}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-md font-semibold text-ink">{bot.name}</p>
          <p className="truncate font-mono text-2xs text-ink-muted">{bot.serverAddress}</p>
        </div>
        <Chip tone={tone} dot pulse={inFlight}>
          {STATE_LABEL[bot.state]}
        </Chip>
      </div>

      {/* Карта + координаты поверх неё. */}
      <div className="relative mx-3.5 mt-3 aspect-16/9 overflow-hidden rounded-sm border border-line bg-abyss">
        <MinimapCanvas frame={minimap} label={`Карта вокруг ${bot.name}`} />
        {coords === null ? null : (
          <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-abyss/90 to-transparent px-2 pt-6 pb-1.5 text-center font-mono text-xs text-ink tabular-nums">
            {coords}
          </span>
        )}
        {minimap === null ? (
          <span className="absolute inset-0 flex items-center justify-center px-3 text-center text-2xs text-ink-faint">
            {running
              ? 'Карта появится, когда прогрузятся чанки'
              : bot.state === 'AUTH_FAILED'
                ? 'Нужна переавторизация аккаунта'
                : 'Бот остановлен'}
          </span>
        ) : null}
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 px-3.5 text-2xs">
        <Fact label="Версия" value={detail?.version ?? null} />
        <Fact
          label="Ping"
          value={detail?.ping === undefined || detail?.ping === null ? null : `${detail.ping} мс`}
        />
        <Fact
          label="Здоровье"
          value={
            detail?.health === undefined || detail?.health === null
              ? null
              : String(Math.round(detail.health))
          }
        />
        <Fact
          label="Сытость"
          value={
            detail?.food === undefined || detail?.food === null
              ? null
              : String(Math.round(detail.food))
          }
        />
        <Fact label="В игре" value={uptimeOf(bot.startedAt)} />
        <Fact label="Автозапуск" value={bot.autoStart ? 'вкл' : 'выкл'} />
      </dl>

      <OnTop className="mt-3 flex items-center gap-1 border-t border-line px-2 py-1.5">
        {running ? (
          <IconButton
            label="Остановить"
            title={noRights ?? 'Остановить'}
            disabled={!canOperate || busy}
            onClick={onStop}
          >
            <Square aria-hidden className="size-4" />
          </IconButton>
        ) : (
          <IconButton
            label="Запустить"
            title={noRights ?? 'Запустить'}
            disabled={!canOperate || busy}
            onClick={onStart}
          >
            <Play aria-hidden className="size-4" />
          </IconButton>
        )}

        {/* Камера — отдельная кнопка, но открывается тем же роутером: тридцать
            WebGL-контекстов на списке невозможны (док 08 §1), поэтому она ведёт
            на карточку бота, а не рисует поток здесь. */}
        <IconButton
          label="Открыть камеру"
          title={bot.state === 'PLAYING' ? 'Открыть камеру' : 'Камера только у бота в игре'}
          disabled={bot.state !== 'PLAYING' || onCamera === undefined}
          onClick={onCamera}
        >
          <Eye aria-hidden className="size-4" />
        </IconButton>

        <Menu
          label={`Действия для ${bot.name}`}
          className="ml-auto"
          items={[
            {
              label: 'Изменить',
              icon: <Pencil aria-hidden className="size-4" />,
              onSelect: onEdit,
              disabled: !canOperate,
              title: noRights
            },
            {
              label: 'Перезапустить',
              icon: <RotateCw aria-hidden className="size-4" />,
              onSelect: onRestart,
              disabled: !canOperate,
              title: noRights
            },
            {
              label: 'Удалить бота',
              icon: <Trash2 aria-hidden className="size-4" />,
              onSelect: onDelete,
              disabled: !canOperate,
              destructive: true,
              title: noRights
            }
          ]}
        />
      </OnTop>
    </Surface>
  )
}

function Fact({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-2">
      <dt className="text-ink-muted">{label}</dt>
      <dd
        className={cn(
          'truncate font-mono tabular-nums',
          value === null ? 'text-ink-faint' : 'text-ink'
        )}
      >
        {value ?? '—'}
      </dd>
    </div>
  )
}

/** L1-срез высот: 2D-канвас 64², растянутый без интерполяции. */
function MinimapCanvas({
  frame,
  label
}: {
  frame: MinimapFrame | null
  label: string
}): JSX.Element | null {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const element = ref.current
    if (element === null || frame === null) return
    const ctx = element.getContext('2d')
    if (ctx === null) return
    ctx.putImageData(
      minimapImageData(frame, (w, h) => ctx.createImageData(w, h)),
      0,
      0
    )
  }, [frame])

  if (frame === null) return null

  return (
    <canvas
      ref={ref}
      width={MINIMAP_SIZE}
      height={MINIMAP_SIZE}
      role="img"
      aria-label={label}
      className="size-full object-cover [image-rendering:pixelated]"
    />
  )
}
